import { useTranslation } from 'react-i18next';

import { DataPanelItem, DataPanelRow } from '@/ui';
import type { PanelItemColor } from '@/ui';
import { InternetConnectionType } from '@/signalr';
import type { DeviceInfo } from '@/signalr';
import { deviceOsDisplayName } from '../../model/deviceOs';
import { internetConnectionTypeToText } from '../../model/internetConnection';

export type DeviceInfoPanelRowProps = {
  deviceInfo: DeviceInfo;
  eedBatteryLevel?: string | null;
};

type FormattedBattery = {
  level: string | null;
  color: PanelItemColor;
};

function formatBattery(deviceInfo: DeviceInfo, eedBatteryLevel: string | null | undefined, language: string): FormattedBattery {
  if (deviceInfo.batteryLevel != null) {
    const batteryColor: PanelItemColor =
      deviceInfo.batteryLevel > 50 ? 'excellent' : deviceInfo.batteryLevel > 20 ? 'warning' : 'critical';
    const formatted = new Intl.NumberFormat(language, { maximumFractionDigits: 0 }).format(deviceInfo.batteryLevel);
    return { level: `${formatted} %`, color: batteryColor };
  }

  if (eedBatteryLevel != null) {
    let batteryLevel = eedBatteryLevel.trim().toLowerCase();
    if (batteryLevel.includes('10%')) {
      batteryLevel = '0-10%';
    }
    const batteryColor: PanelItemColor =
      batteryLevel === '61-100%' || batteryLevel === '41-60%'
        ? 'excellent'
        : batteryLevel === '21-40%'
          ? 'warning'
          : 'critical';

    return { level: eedBatteryLevel, color: batteryColor };
  }

  return { level: null, color: 'none' };
}

function internetConnectionColor(connectionType: InternetConnectionType): PanelItemColor {
  switch (connectionType) {
    case InternetConnectionType.Unknown:
      return 'none';
    case InternetConnectionType.NoConnection:
    case InternetConnectionType.G2:
      return 'critical';
    case InternetConnectionType.G3:
      return 'warning';
    case InternetConnectionType.G4:
    case InternetConnectionType.G5:
    case InternetConnectionType.WiFi:
      return 'excellent';
    default:
      return 'none';
  }
}

export function DeviceInfoPanelRow({ deviceInfo, eedBatteryLevel }: DeviceInfoPanelRowProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'en';

  const battery = formatBattery(deviceInfo, eedBatteryLevel, language);

  return (
    <DataPanelRow>
      <DataPanelItem
        title={t('Incident_InfoPanel_BatteryLevel')}
        value={battery.level ?? undefined}
        color={battery.color}
        showEmptyPlaceholder
      />

      <DataPanelItem
        title={t('Incident_InfoPanel_InternetConnectionStrength')}
        value={internetConnectionTypeToText(deviceInfo.internetConnectionType, language)}
        color={internetConnectionColor(deviceInfo.internetConnectionType)}
        showEmptyPlaceholderWhen={() => deviceInfo.internetConnectionType === InternetConnectionType.Unknown}
      />

      <DataPanelItem title={t('Incident_InfoPanel_System')} value={deviceOsDisplayName(deviceInfo.system)} />
    </DataPanelRow>
  );
}
